import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import {
  Activity,
  BarChart3,
  Building2,
  Calendar,
  Flame,
  Home,
  MessagesSquare,
  Moon,
  Presentation,
  Search,
  Star,
  Store,
  Sun,
  TrendingUp,
} from 'lucide-react';
import { useDashboard } from '@/hooks/useDashboardContext';
import { useOutlets } from '@/hooks/useOutlets';
import { useTheme } from '@/hooks/useTheme';
import type { PeriodKey } from '@/lib/periods';

interface NavItem {
  id: string;
  label: string;
  icon: typeof Home;
  path: string;
}

const NAV: NavItem[] = [
  { id: 'today', label: 'Today', icon: Sun, path: '/' },
  { id: 'overview', label: 'Overview', icon: Home, path: '/overview' },
  { id: 'review', label: 'Business Review', icon: Presentation, path: '/review' },
  { id: 'outlets', label: 'Outlets', icon: Building2, path: '/outlets' },
  { id: 'sales', label: 'Sales', icon: BarChart3, path: '/sales' },
  { id: 'aggregators', label: 'Aggregators', icon: TrendingUp, path: '/aggregators' },
  { id: 'ratings', label: 'Ratings', icon: Star, path: '/ratings' },
  { id: 'reviews', label: 'Reviews', icon: MessagesSquare, path: '/reviews' },
  { id: 'meta', label: 'Meta', icon: Activity, path: '/meta' },
  { id: 'alerts', label: 'Alerts', icon: Flame, path: '/alerts' },
];

const PERIODS: { id: PeriodKey; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7 days' },
  { id: 'last_month', label: 'Last month' },
  { id: 'month', label: 'This month' },
  { id: 'quarter', label: 'This quarter' },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { setOutletId, setPeriod } = useDashboard();
  const { data: outlets = [] } = useOutlets();
  const { setTheme } = useTheme();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const run = useCallback((fn: () => void) => {
    fn();
    setOpen(false);
    setQuery('');
  }, []);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[20vh] z-50 w-[92vw] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-popover shadow-2xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>
          <Command shouldFilter className="flex flex-col">
            <div className="flex items-center gap-2 border-b border-border/60 px-3">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Jump to outlet, page, change period…"
                className="flex h-12 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              />
              <kbd className="hidden text-2xs text-muted-foreground sm:inline">esc</kbd>
            </div>
            <Command.List className="max-h-[60vh] overflow-y-auto p-2">
              <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
                No matches.
              </Command.Empty>

              <Command.Group heading="Outlets" className="text-2xs uppercase text-muted-foreground">
                <PaletteItem
                  icon={Store}
                  onSelect={() =>
                    run(() => {
                      setOutletId('all');
                      navigate('/overview');
                    })
                  }
                  label="All outlets"
                  hint="overview"
                />
                {outlets.map((o) => (
                  <PaletteItem
                    key={o.id}
                    icon={Store}
                    onSelect={() =>
                      run(() => {
                        setOutletId(o.id);
                        navigate(`/outlets/${o.id}`);
                      })
                    }
                    label={o.name}
                    hint={o.city}
                  />
                ))}
              </Command.Group>

              <Command.Group heading="Pages" className="mt-2 text-2xs uppercase text-muted-foreground">
                {NAV.map((n) => (
                  <PaletteItem
                    key={n.id}
                    icon={n.icon}
                    onSelect={() => run(() => navigate(n.path))}
                    label={n.label}
                  />
                ))}
              </Command.Group>

              <Command.Group heading="Period" className="mt-2 text-2xs uppercase text-muted-foreground">
                {PERIODS.map((p) => (
                  <PaletteItem
                    key={p.id}
                    icon={Calendar}
                    onSelect={() => run(() => setPeriod(p.id))}
                    label={`Set period: ${p.label}`}
                  />
                ))}
              </Command.Group>

              <Command.Group heading="Settings" className="mt-2 text-2xs uppercase text-muted-foreground">
                <PaletteItem
                  icon={Moon}
                  onSelect={() => run(() => setTheme('dark'))}
                  label="Use dark theme"
                />
                <PaletteItem
                  icon={Sun}
                  onSelect={() => run(() => setTheme('light'))}
                  label="Use light theme"
                />
                <PaletteItem
                  icon={Sun}
                  onSelect={() => run(() => setTheme('sun'))}
                  label="Auto (sunset → dark)"
                />
              </Command.Group>
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PaletteItem({
  icon: Icon,
  label,
  hint,
  onSelect,
}: {
  icon: typeof Home;
  label: string;
  hint?: string;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm aria-selected:bg-accent aria-selected:text-accent-foreground"
    >
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="flex-1">{label}</span>
      {hint && <span className="text-2xs text-muted-foreground">{hint}</span>}
    </Command.Item>
  );
}
